<template>
  <v-container class="sidebar-container">
    <transition name="fade" mode="out-in">
      <v-card v-if="page !== 'PaymentInformation' && page !== 'GuestPaymentInformation'" class="summary-card elevation-2">
        <div class="summary-header">
          <h1 class="summary-title earthwerk-font">BOOKING SUMMARY</h1>
          <h3 class="summary-dates">
            {{ formattedCheckIn }} {{ formattedCheckOut }}
            <span class="nights-badge">{{ totalNights }} NIGHT(S)</span>
          </h3>
        </div>

        <div class="summary-sections">
          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-nature-people</v-icon>
              <h3 class="section-title earthwerk-font">CONSERVATION ENTRY FEES</h3>
            </div>
            <div v-if="allConservationFees.filter(g => g.payment_guests > 0).length === 0" class="empty-state">
              No conservation fees
            </div>
            <div v-else>
              <div v-for="(guest, key) in allConservationFees"
                   :key="key"
                   v-if="guest.payment_guests > 0"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ guest.payment_person }}</span>
                  <span class="item-quantity">{{ guest.payment_guests }} guest(s) × {{ totalNights }} nights</span>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ formatNumber(calculateConservationTotal(guest)) }}
                </div>
                <v-btn v-if="showDeleteButtons && false" icon small class="delete-btn" @click="removeVehicle(key)">
                  v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ formatNumber(conservationTotal) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="$vehicleDisabled === false" class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-car</v-icon>
              <h3 class="section-title earthwerk-font">VEHICLE FEES</h3>
            </div>
            <div v-if="allVehicles.filter(v => v.vehicles > 0).length === 0" class="empty-state">
              No vehicle fees
            </div>
            <div v-else>
              <div v-for="(vehicle, key) in allVehicles"
                   :key="key"
                   v-if="vehicle.vehicles > 0"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ vehicle.vehicle_charge_category }}</span>
                  <span class="item-quantity">{{ vehicle.vehicles }} vehicle(s) × {{ totalNights }} nights</span>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ formatNumber(calculateVehicleTotal(vehicle)) }}
                </div>
                <v-btn v-if="showDeleteButtons && false" icon small class="delete-btn" @click="removeConservationFee(key)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ formatNumber(vehicleTotal) }}
                </span>
              </div>
            </div>
          </div>

          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-bed</v-icon>
              <h3 class="section-title earthwerk-font">ACCOMMODATION</h3>
            </div>
            <div v-if="chosenFacilities.length === 0" class="empty-state">
              No accommodation selected
            </div>
            <div v-else>
              <div v-for="(facility, key) in chosenFacilities"
                   :key="key"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ facility.name }}</span>
                  <span class="item-quantity"> {{ getDays(facility.start_date, facility.end_date) }} nights</span>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ Math.round(facilityTotal(facility)).toLocaleString("en") }}
                </div>
                <v-btn v-if="showDeleteButtons"
                       icon
                       small
                       color="error"
                       class="delete-btn ml-2"
                       @click="removeFacility(facility)"
                       aria-label="Remove accommodation">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ Math.round(allFacilityTotal).toLocaleString("en") }}
                </span>
              </div>
            </div>
          </div>

          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-binoculars</v-icon>
              <h3 class="section-title earthwerk-font">CONSERVATION ACTIVITIES</h3>
            </div>
            <div v-if="chosenInventory.length === 0" class="empty-state">
              No activities selected
            </div>
            <div v-else>
              <div v-for="(inventory, index) in chosenInventory"
                   :key="index"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ inventory.name }}</span>
                  <span class="item-date">{{ formated_date(inventory.date) }}</span>
                  <div v-if="inventory.ranger_marker === false && inventory.adults > 0 && inventory.children > 0"
                       class="item-participants">
                    {{ inventory.residency_adult.payment_person }}: {{ inventory.adults }},
                    {{ inventory.residency_children.payment_person }}: {{ inventory.children }}
                  </div>
                  <div v-else-if="inventory.ranger_marker === false && inventory.adults > 0"
                       class="item-participants">
                    {{ inventory.residency_adult.payment_person }}: {{ inventory.adults }}
                  </div>
                  <div v-else-if="inventory.ranger_marker === false && inventory.children > 0"
                       class="item-participants">
                    {{ inventory.residency_children.payment_person }}: {{ inventory.children }}
                  </div>
                  <div v-else-if="inventory.ranger_marker === true"
                       class="item-participants">
                    Rangers: {{ inventory.adults }}
                  </div>
                </div>
                <div class="item-price">
                  {{ browserCurrency }} {{ Math.round(findActivityTotal(inventory)).toLocaleString("en") }}
                </div>
                <v-btn v-if="showDeleteButtons"
                       icon
                       small
                       color="error"
                       class="delete-btn ml-2"
                       @click="removeInventory(index)"
                       aria-label="Remove activity">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div class="section-total">
                <span>Subtotal</span>
                <span class="total-amount">
                  {{ browserCurrency }} {{ Math.round(allInventoryTotal).toLocaleString("en") }}
                </span>
              </div>
            </div>
          </div>

          <div class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-heart</v-icon>
              <h3 class="section-title earthwerk-font">DONATIONS</h3>
            </div>
            <div v-if="allPublicDonations.length <= 0" class="empty-state">
              No donations
            </div>
            <div v-else>
              <div v-for="(donation, index) in allPublicDonations"
                   :key="index"
                   class="item-row">
                <div class="item-details">
                  <span class="item-name">{{ donation.cause }}</span>
                </div>
                <div class="item-price">
                  {{ donation.currency_name }} {{ donation.amount }}
                </div>
                <v-btn v-if="showDeleteButtons" icon small class="delete-btn" @click="removeDonation(index)">
                  <v-icon small color="white">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>

          <div v-if="proofOfResidency.length > 0" class="summary-section">
            <div class="section-header">
              <v-icon class="section-icon">mdi-account-card-details</v-icon>
              <h3 class="section-title earthwerk-font">PROOF OF RESIDENCY</h3>
            </div>
            <div v-for="(proof, index) in proofOfResidency" :key="index" class="residency-item">
              <div v-if="proof.first_name != '' && proof.last_name!='' && proof.document_id!=''"
                   class="residency-details">
                <div class="residency-name">{{ proof.first_name }} {{ proof.last_name }}</div>
                <div class="residency-id">{{ proof.payment_person }} {{ proof.document_id }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="grand-total">
          <v-divider class="total-divider"></v-divider>
          <div class="total-amount-row">
            <span>TOTAL AMOUNT</span>
            <span class="amount">{{ browserCurrency }} {{ formatNumber(totalPrice) }}</span>
          </div>
          
          <!-- LOGGED IN: COMPLETE BOOKING -->
<!-- LOGGED IN: PROCEED TO CHECKOUT -->
<div v-if="isLoggedIn" class="checkout-section">
  <v-btn 
    block 
    x-large 
    color="#74a533" 
    dark 
    class="checkout-btn earthwerk-font"
    @click="goToCheckout"
    style="border-radius: 40px !important;"
  >
    <v-icon left size="24">mdi-arrow-right</v-icon>
    PROCEED TO CHECKOUT
  </v-btn>
</div>

          <!-- GUEST: CLOSE BUTTON -->
          <div v-else class="checkout-section">
          <v-btn 
              block 
              x-large 
              color="#74a533" 
              dark
              class="checkout-btn earthwerk-font"
              @click="handleClose"
              style="border-radius: 40px !important;"
              >
            <v-icon left size="24">mdi-arrow-right</v-icon>
            CONTINUE
          </v-btn>
          </div>
        </div>
      </v-card>

      <v-card v-else class="payment-card elevation-2">
        <div class="payment-header">
          <h2 class="payment-title earthwerk-font">BOOKING TOTAL</h2>
        </div>

        <div v-if="paymentUSDPrice === undefined" class="loading-container">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>

        <div v-else class="payment-details">
          <div class="payment-amount">
            <span class="currency">{{ bookingCurrency || browserCurrency }}</span>
            <span class="amount">{{ $roundOff(totalInBookingCurrency) }}</span>
          </div>

          <div v-if="allOnlinepaymentDonations.length > 0" class="donation-section">
            <h4 class="donation-title earthwerk-font">CONSERVATION DONATION</h4>
            <div v-for="(donation, index) in allOnlinepaymentDonations"
                 :key="index"
                 class="donation-item">
              <span class="donation-amount">
                {{ donation.new_donation_currency_name }} {{ donation.new_donation_amount }}
              </span>
              <v-btn v-if="showDeleteButtonsOnPayment" 
                     icon small
                     class="delete-btn donation"
                     @click="removeOnlineDonation(index)">
                <v-icon small color="white">mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </transition>
  </v-container>
</template>

<script>
import Guests from "./Guests";

export default {
  name: "BookingSummary",
  components: {
    Guests,
  },
  data() {
    return {
      route: "",
      page: this.$route.name,
      total: 0,
      date_settings: {
        night: "Night",
        nights: "Nights",
        "day-names": ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"],
        "check-in": "Check-in",
        "check-out": "Check-Out",
        "month-names": [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ],
      },
      reservation: {
        days: 0,
        currency: localStorage.getItem("currency"),
      },
      booking: false,
      donations: localStorage.getItem("donation"),
      localStorageTick: 0,
    };
  },
  computed: {
    isLoggedIn() {
      const userName = localStorage.getItem('user_name');
      return userName && userName !== 'null' && userName !== '';
    },

    showDeleteButtons() {
      const routeName = this.$route.name;
      return routeName !== 'GuestPaymentInformation';
    },

    showDeleteButtonsOnPayment() {
      const routeName = this.$route.name;
      return routeName !== 'GuestPaymentInformation';
    },

    loggedInUserName() {
      return localStorage.getItem('user_name') || '';
    },

    loggedInUserEmail() {
      return localStorage.getItem('user_email') || '';
    },

    loggedInUserCountry() {
      return localStorage.getItem('user_country') || '';
    },

    loggedInUserCity() {
      return localStorage.getItem('user_city') || '';
    },

    globalBookingDates() {
      if (this.$store.state.GlobalBookingStore) {
        return this.$store.state.GlobalBookingStore.booking_dates;
      }
      return null;
    },

    globalGuests() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_guests) {
        return this.$store.state.GlobalBookingStore.booking_guests;
      }
      return { adults: 0, children: 0, total: 0 };
    },

    chosenFacilities() {
      this.localStorageTick;
      try {
        return JSON.parse(localStorage.getItem('facility_data') || '[]');
      } catch (e) {
        return [];
      }
    },

    chosenInventory() {
      this.localStorageTick;
      try {
        return JSON.parse(localStorage.getItem('activity_data') || '[]');
      } catch (e) {
        return [];
      }
    },

    allConservationFees() {
      this.localStorageTick;
      try {
        return JSON.parse(localStorage.getItem('conservation_data') || '[]');
      } catch (e) {
        return [];
      }
    },

    allVehicles() {
      this.localStorageTick;
      try {
        return JSON.parse(localStorage.getItem('vehicle_data') || '[]');
      } catch (e) {
        return [];
      }
    },

    formattedCheckIn() {
      this.localStorageTick; 
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}');
        if (booking.check_in) {
          return this.$moment(booking.check_in).format('MMM DD, YYYY');
        }
      } catch (e) {}

      if (this.globalBookingDates && this.globalBookingDates.check_in) {
        return this.$moment(this.globalBookingDates.check_in).format('MMM DD, YYYY');
      }
      
      const userData = JSON.parse(localStorage.getItem("user_data") || '{}');
      if (userData.check_in) {
        return this.$moment(userData.check_in).format('MMM DD, YYYY');
      }
      
      return 'Select dates';
    },
    
    formattedCheckOut() {
      this.localStorageTick; 
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}');
        if (booking.check_out) {
          return this.$moment(booking.check_out).format('MMM DD, YYYY');
        }
      } catch (e) {}

      if (this.globalBookingDates && this.globalBookingDates.check_out) {
        return this.$moment(this.globalBookingDates.check_out).format('MMM DD, YYYY');
      }
      
      const userData = JSON.parse(localStorage.getItem("user_data") || '{}');
      if (userData.check_out) {
        return this.$moment(userData.check_out).format('MMM DD, YYYY');
      }
      
      return 'Select dates';
    },
    
    totalNights: function () {
      this.localStorageTick; 
      try {
        const booking = JSON.parse(localStorage.getItem('booking') || '{}');
        if (booking.check_in && booking.check_out) {
          return Math.max(1, this.getDays(booking.check_in, booking.check_out));
        }
      } catch (e) {}

      if (this.globalBookingDates && this.globalBookingDates.check_in && this.globalBookingDates.check_out) {
        return Math.max(1, this.getDays(this.globalBookingDates.check_in, this.globalBookingDates.check_out));
      }
      
      const userData = JSON.parse(localStorage.getItem("user_data") || '{}');
      if (userData.check_in && userData.check_out) {
        return Math.max(1, this.getDays(userData.check_in, userData.check_out));
      }
      
      return 1;
    },
    
    conservationTotal: function () {
      let total = 0;
      if (this.allConservationFees && this.allConservationFees.length > 0) {
        for (let i = 0; i < this.allConservationFees.length; i++) {
          const guest = this.allConservationFees[i];
          if (guest.payment_guests > 0) {
            total += this.calculateConservationTotal(guest);
          }
        }
      }
      return total;
    },
    
    vehicleTotal: function () {
      let total = 0;
      if (this.allVehicles && this.allVehicles.length > 0) {
        for (let i = 0; i < this.allVehicles.length; i++) {
          const vehicle = this.allVehicles[i];
          if (vehicle.vehicles > 0) {
            total += this.calculateVehicleTotal(vehicle);
          }
        }
      }
      return total;
    },
    
    allFacilityTotal: function () {
      let total = 0;
      if (this.chosenFacilities && this.chosenFacilities.length > 0) {
        for (let i = 0; i < this.chosenFacilities.length; i++) {
          total += this.facilityTotal(this.chosenFacilities[i]);
        }
      }
      return total;
    },
    
    allInventoryTotal: function () {
      let total = 0;
      if (this.chosenInventory && this.chosenInventory.length > 0) {
        for (let i = 0; i < this.chosenInventory.length; i++) {
          total += this.findActivityTotal(this.chosenInventory[i]);
        }
      }
      return total;
    },
    
    totalOnlineDonations: function () {
      let total = 0;
      if (this.allOnlinepaymentDonations && this.allOnlinepaymentDonations.length > 0) {
        for (let i = 0; i < this.allOnlinepaymentDonations.length; i++) {
          total += Number(this.allOnlinepaymentDonations[i].new_donation_amount);
        }
      }
      return total;
    },
    
    totalPrice: function () {
      let donationAmount = 0;
      if (this.allPublicDonations && this.allPublicDonations.length > 0) {
        if (this.allPublicDonations[0].currency === this.browserCurrencyID) {
          donationAmount = Number(this.allPublicDonations[0].amount);
        } else {
          if (this.browserCurrencyID === "6bf74e49-7ff6-4555-a080-15541d62204a") {
            donationAmount = Number(this.allPublicDonations[0].amount);
          } else {
            donationAmount = Number(
              this.convertBasedBookingCreationDate(
                this.allPublicDonations[0].currency_name,
                Number(this.allPublicDonations[0].amount),
                this.allPublicDonations[0].currency_buy_rate,
                this.allPublicDonations[0].currency_sell_rate
              ).amount
            );
          }
        }
      }

      return this.allInventoryTotal + this.allFacilityTotal + this.vehicleTotal + this.conservationTotal + donationAmount;
    },
    
    paymentKESPrice: function () {
      if (this.singleBooking && this.singleBooking.currency !== undefined) {
        var total = 0;
        total = this.convert2(
          this.singleBooking.currency,
          "KES",
          this.singleBooking.booking_payment_balance
        ).amount;
        return total;
      }
      return 0;
    },
    
    paymentUSDPrice: function () {
      let donationAmount = this.totalOnlineDonations || 0;
      if (this.singleBooking && this.singleBooking.currency !== undefined) {
        var total = 0;
        total = Number(
          this.convertBasedBookingCreationDate(
            this.singleBooking.currency,
            this.singleBooking.booking_payment_balance,
            this.singleBooking.currency_buying_rate_at_time,
            this.singleBooking.currency_selling_rate_at_time
          ).amount
        );
        total = total + Math.ceil(donationAmount);
        return total;
      }
      return 0;
    },

    bookingCurrency: function () {
      if (this.singleBooking) {
        return this.singleBooking.currency || 
               this.singleBooking.booking_currency_name || 
               'USD';
      }
      return 'USD';
    },

    totalInBookingCurrency: function () {
      if (this.singleBooking && this.singleBooking.currency !== undefined) {
        var total = this.singleBooking.booking_payment_balance || 0;
        var donationAmount = this.totalOnlineDonations || 0;
        
        if (this.allOnlinepaymentDonations && this.allOnlinepaymentDonations.length > 0) {
          for (var i = 0; i < this.allOnlinepaymentDonations.length; i++) {
            var donation = this.allOnlinepaymentDonations[i];
            if (donation.new_donation_currency_name === this.bookingCurrency) {
              total += Number(donation.new_donation_amount);
            } else {
              var converted = this.convertCurrency(
                donation.new_donation_currency_name,
                this.bookingCurrency,
                Number(donation.new_donation_amount)
              );
              total += converted;
            }
          }
        }
        
        return total;
      }
      return 0;
    },

    bookingCurrencyId: function () {
      if (this.singleBooking) {
        if (this.singleBooking.currency === 'KES') {
          return '162fface-f5f1-41de-913b-d2bb784dda3a';
        } else if (this.singleBooking.currency === 'USD') {
          return '6bf74e49-7ff6-4555-a080-15541d62204a';
        }
      }
      return '6bf74e49-7ff6-4555-a080-15541d62204a';
    }
  },
  created() {
    if (this.$route.name !== "PaymentInformation") {
      this.route = this.$route.name;
    }
    this.$root.$on('cart-updated', this.refresh)
    window.addEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    this.$root.$off('cart-updated', this.refresh)
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    refresh() {
      this.localStorageTick++
      this.$forceUpdate()
    },

    handleStorageChange(event) {
      const keys = ['activity_data', 'facility_data', 'conservation_data', 'vehicle_data', 'vehicle_separate', 'guests', 'booking']
      if (keys.includes(event.key)) {
        this.localStorageTick++
        this.$forceUpdate()
      }
    },

    formatNumber(value) {
      return Math.round(value).toLocaleString("en");
    },
    
    convertCurrency: function (fromCurrency, toCurrency, amount) {
      if (!amount || amount === 0) return 0;
      
      fromCurrency = fromCurrency.toUpperCase();
      toCurrency = toCurrency.toUpperCase();
      
      if (fromCurrency === toCurrency) return amount;
      
      var exchanges = this.allExchanges || [];
      var result = amount;
      
      if (fromCurrency !== 'KES') {
        var fromRate = 1;
        exchanges.forEach(function(exchange) {
          if (exchange.currency && exchange.currency.toUpperCase() === fromCurrency) {
            fromRate = parseFloat(exchange.sell_amount) || 1;
          }
        });
        result = result * fromRate;
      }
      
      if (toCurrency !== 'KES') {
        var toRate = 1;
        exchanges.forEach(function(exchange) {
          if (exchange.currency && exchange.currency.toUpperCase() === toCurrency) {
            toRate = parseFloat(exchange.buy_amount) || 1;
          }
        });
        result = result / toRate;
      }
      
      return result;
    },
    
    calculateConservationTotal(guest) {
      if (this.convert1) {
        let dailyTotal = this.convert1(
          guest.payment_person_currency,
          guest.payment_person_amount
        ).amount * guest.payment_guests;
        return dailyTotal * this.totalNights;
      }
      return 0;
    },
    
    calculateVehicleTotal(vehicle) {
      if (this.convert1) {
        let dailyTotal = this.convert1(
          vehicle.vehicle_charge_cost_currency,
          vehicle.vehicle_charge_category_cost
        ).amount * Number(vehicle.vehicles);
        return dailyTotal * this.totalNights;
      }
      return 0;
    },
    
    getFacilityDays(facility) {
      if (facility.start_date && facility.end_date) {
        return Number(this.getDays(facility.start_date, facility.end_date));
      }
      return this.totalNights;
    },

    facilityTotal: function (facility) {
      if (!facility) return 0;

      const nights = this.getFacilityDays(facility);
      const discount = Number(facility.accommodation_discount || 0) / 100;

      const adults        = Number(facility.adults || 0);
      const children      = Number(facility.children || 0);
      const extraAdults   = Number(facility.extra_pax_adults || 0);
      const extraChildren = Number(facility.extra_pax_children || 0);

      const standardAdults   = Math.max(0, adults - extraAdults);
      const standardChildren = Math.max(0, children - extraChildren);

      const accType   = String(facility.accommodation_type || '').toLowerCase();
      const maxGuests = Number(facility.maximum_guests || 0);

      const adultRate = Number(
        facility.adult_price ||
        facility.mandatory_price ||
        0
      );
      const childRate = Number(facility.child_price || 0);
      const extraAdultRate = Number(
        facility.cost_per_extra_adult ||
        facility.mandatory_price ||
        facility.adult_price ||
        0
      );
      const extraChildRate = Number(
        facility.cost_per_extra_child ||
        facility.child_price ||
        0
      );

      let total = 0;

      if (accType === 'stables' && maxGuests === 1) {
        const roomRate = Number(facility.mandatory_price || adultRate);
        total =
          (standardAdults + standardChildren) * roomRate * nights +
          (extraAdults + extraChildren) * roomRate * nights;
      }

      else if (accType === 'stables' && maxGuests >= 2) {
        const rooms = Number(facility.number_of_rooms || 1);
        const roomRate = Number(facility.mandatory_price || 0);

        const baseByRoom = roomRate * rooms * nights;
        const baseByPerson =
          standardAdults * adultRate * nights +
          standardChildren * childRate * nights;

        total = baseByRoom > 0 ? baseByRoom : baseByPerson;
        total +=
          extraAdults * extraAdultRate * nights +
          extraChildren * extraChildRate * nights;
      }

      else if (accType === 'pelican') {
        total =
          standardAdults * adultRate * nights +
          standardChildren * childRate * nights +
          extraAdults * extraAdultRate * nights +
          extraChildren * extraChildRate * nights;
      }

      else {
        total =
          standardAdults * adultRate * nights +
          standardChildren * childRate * nights +
          extraAdults * extraAdultRate * nights +
          extraChildren * extraChildRate * nights;
      }

      if (discount > 0) total = total * (1 - discount);

      if (facility.currency && this.convert1) {
        return this.convert1(facility.currency, total).amount;
      }
      return total;
    },
    findActivityTotal: function (inventory) {
      let total = 0;
      
      if (inventory.residency_adult && inventory.residency_adult.price_per_person) {
        total += inventory.adults * inventory.residency_adult.price_per_person;
      }
      
      if (inventory.residency_children && inventory.residency_children.price_per_person) {
        total += inventory.children * inventory.residency_children.price_per_person;
      }
      
      if (inventory.adult_price && inventory.adults) {
        total += inventory.adult_price * inventory.adults;
      }
      
      if (inventory.child_price && inventory.children) {
        total += inventory.child_price * inventory.children;
      }
      
      if (total > 0 && inventory.currency && this.convert1) {
        total = this.convert1(inventory.currency, total).amount;
      }
      
      return total;
    },

    getDays: function (start, end) {
      var startDate = this.$moment(start);
      var endDate = this.$moment(end);
      var days = endDate.diff(startDate, "days");
      return days > 0 ? days : 1;
    },
    
    convertConservation: function (guest) {
      return this.calculateConservationTotal(guest);
    },
    
    convertVehicle: function (data) {
      var total = this.calculateVehicleTotal(data);
      if (this.$store.state.CurrencyStore) {
        return (
          this.$store.state.CurrencyStore.browser_currency +
          " " +
          Number(total).toLocaleString("en")
        );
      }
      return total;
    },
    
    removeConservationFee: function (key) {
      const fees = JSON.parse(localStorage.getItem("conservation_data") || '[]')
      fees.splice(key, 1)
      localStorage.setItem("conservation_data", JSON.stringify(fees))
      this.$root.$emit('cart-updated')
      this.refresh()
    },
    
    removeVehicle: function (key) {
      const vehicles = JSON.parse(localStorage.getItem("vehicle_data") || '[]')
      vehicles.splice(key, 1)
      localStorage.setItem("vehicle_data", JSON.stringify(vehicles))
      this.$root.$emit('cart-updated')
      this.refresh()
    },
    
    removeFacility: function (facility) {
      const facilityData = JSON.parse(localStorage.getItem("facility_data") || '[]');
      const updated = facilityData.filter(function(f) {
        return f.public_id !== facility.public_id;
      });
      localStorage.setItem("facility_data", JSON.stringify(updated));
      this.$root.$emit('cart-updated')
      this.refresh()
    },
    
    removeInventory: function (index) {
      const activityData = JSON.parse(localStorage.getItem("activity_data") || '[]');
      activityData.splice(index, 1);
      localStorage.setItem("activity_data", JSON.stringify(activityData));
      this.$root.$emit('cart-updated')
      this.refresh()
    },
    
    removeDonation: function (index) {
      const donations = JSON.parse(localStorage.getItem("donation_data") || '[]');
      donations.splice(index, 1);
      localStorage.setItem("donation_data", JSON.stringify(donations));
      this.refresh()
    },
    
    removeOnlineDonation: function (index) {
      const donations = JSON.parse(localStorage.getItem("online_donation_data") || '[]');
      donations.splice(index, 1);
      localStorage.setItem("online_donation_data", JSON.stringify(donations));
      this.refresh()
    },
    
    updateLocalStorage: function () {
      if (this.allConservationFees) {
        localStorage.setItem("conservation_data", JSON.stringify(this.allConservationFees));
      }
      if (this.allVehicles) {
        localStorage.setItem("vehicle_data", JSON.stringify(this.allVehicles));
      }
    },
    
    formated_date: function (date) {
      return this.$moment(date).format("MMM DD, YYYY");
    },

    handleClose() {
      // Force close for both the immediate parent and the root
      this.$emit('close');
      if (this.$parent) {
        this.$parent.$emit('close');
      }
    },
    
    goToCheckout: function () {
      this.saveCheckoutData();
      this.$router.push({ name: 'GuestCheckout' });
    },
    
    saveCheckoutData: function () {
      const userData = JSON.parse(localStorage.getItem("user_data") || '{}');
      let checkIn = null;
      let checkOut = null;
      
      try {
        const booking = JSON.parse(localStorage.getItem("booking") || '{}');
        checkIn = booking.check_in;
        checkOut = booking.check_out;
      } catch(e) {}

      if (!checkIn && this.globalBookingDates) {
        checkIn = this.globalBookingDates.check_in;
        checkOut = this.globalBookingDates.check_out;
      }
      
      if (!checkIn) {
        checkIn = userData.check_in;
        checkOut = userData.check_out;
      }
      
      const bookingData = {
        check_in: checkIn,
        check_out: checkOut,
        days: this.totalNights,
        currency: this.browserCurrency,
        total: this.totalPrice,
        guests: this.globalGuests
      };
      
      localStorage.setItem('booking', JSON.stringify(bookingData));
      localStorage.setItem('booking_total', this.totalPrice.toString());
      
      if (this.$store.commit) {
        this.$store.commit('SET_BOOKING_TOTAL', this.totalPrice);
      }
    },
    
    checkInDate: function (data) {
      var date = data;
      date = this.$moment(date).format("YYYY-MM-DD");
      this.reservation.check_in = date;
    },
    
    checkOutDate: function (data) {
      var date = data;
      date = this.$moment(date).format("YYYY-MM-DD");
      this.reservation.check_out = date;
      if (this.adjustDates) {
        this.adjustDates(this.reservation.check_in, this.reservation.check_out);
      }
    },
    
    selectAccomodation: function () {
      var selectedAccomodation = {
        public_id: this.singleFacility.public_id,
        facility_type: this.singleFacility.facility_type_id,
        accommodation_type: this.singleFacility.accomodation_type_id,
        analysis_code: this.singleFacility.analysis_code,
        start_date: JSON.parse(localStorage.getItem("user_data")).check_in,
        end_date: JSON.parse(localStorage.getItem("user_data")).check_out,
        name: this.singleFacility.name,
        adult_price: this.singleFacility.price.adult_price,
        child_price: this.singleFacility.price.child_price,
        mandatory_price: this.singleFacility.price.mandatory_price,
        children: 0,
        adults: this.getCoservationGuests,
        currency: this.singleFacility.price.currency_type,
      };
      localStorage.setItem(
        "facility_data",
        JSON.stringify([selectedAccomodation])
      );
      if (this.$store.commit) {
        this.$store.commit("BROWSER_FACILITIES", [selectedAccomodation]);
      }
      this.$router.push({
        name: "ActivitySelection",
      });
    }
  },
  
  watch: {
    globalBookingDates: {
      handler: function(newDates) {
        if (newDates && newDates.check_in && newDates.check_out) {
          this.$forceUpdate();
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  /* ==================================================
     RESPONSIVE LAYOUT & FLEXBOX FIXES
  ================================================== */
  .sidebar-container {
    padding: 0;
    max-width: 100%;
    /* Use 100dvh to account for mobile browser address bars */
    height: 100dvh; 
    max-height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden; /* Prevent the whole page from scrolling */
  }

  .summary-card, .payment-card {
    border-radius: 12px !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08) !important;
    background: white;
    transition: all 0.3s ease;
    
    /* Flexbox settings to pin the footer */
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;
    min-height: 0; 
    overflow: hidden; 
  }

  .summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
  }

  .summary-header, .payment-header {
    padding: 24px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    flex-shrink: 0; /* Prevents header from shrinking */
  }

  .summary-title, .payment-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    text-align: center;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .summary-dates {
    font-size: 16px;
    font-weight: 500;
    color: #4a5568;
    margin: 8px 0 0;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .nights-badge {
    background: rgba(116, 165, 51, 0.9);
    color: white;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
  }

  /* Middle scrolls */
  .summary-sections {
    flex: 1; /* Takes up all remaining space */
    min-height: 0; /* CRITICAL: Allows flex child to shrink and scroll */
    overflow-y: auto;
    padding: 0 24px;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  }

  /* Scrollbar polish */
  .summary-sections::-webkit-scrollbar {
    width: 6px;
  }
  .summary-sections::-webkit-scrollbar-track {
    background: transparent;
  }
  .summary-sections::-webkit-scrollbar-thumb {
    background: rgba(116, 165, 51, 0.35);
    border-radius: 3px;
  }

  .summary-section {
    padding: 20px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  .summary-section:last-child {
    border-bottom: none;
  }

  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  .section-icon {
    margin-right: 12px;
    color: #74a533;
  }

  .section-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .empty-state {
    color: #a0aec0;
    font-size: 14px;
    text-align: center;
    padding: 12px 0;
    font-style: italic;
  }

  .item-row {
    display: flex;
    align-items: center;
    padding: 12px 0;
    position: relative;
    transition: all 0.2s ease;
  }

  .item-row:hover {
    background-color: rgba(245, 247, 250, 0.5);
  }

  .item-details {
    flex: 1;
    min-width: 0;
  }

  .item-name {
    font-weight: 600;
    color: #2d3748;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-quantity, .item-date, .item-participants {
    font-size: 12px;
    color: #718096;
    margin-top: 4px;
  }

  .guest-detail, .extra-detail {
    font-size: 11px;
    color: #74a533;
    margin-left: 4px;
  }

  .extra-detail {
    color: #e67e22;
  }

  .item-price {
    font-weight: 600;
    color: #2d3748;
    margin-left: 16px;
    white-space: nowrap;
  }

  .delete-btn {
    margin-left: 12px;
    background: #ff4444 !important;
    color: white !important;
    border-radius: 50%;
    width: 28px !important;
    height: 28px !important;
    box-shadow: 0 2px 6px rgba(204, 0, 0, 0.3);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  .delete-btn:hover {
    transform: scale(1.1);
    background: #ff3333 !important;
  }

  .delete-btn:active {
    transform: scale(0.95);
  }

  .delete-btn.donation {
    background: #ff6666 !important;
  }

  .section-total {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    font-weight: 500;
    color: #4a5568;
    border-top: 1px dashed rgba(0, 0, 0, 0.1);
    margin-top: 8px;
  }

  .total-amount {
    color: #2d3748;
    font-weight: 600;
  }

  .residency-item {
    padding: 12px 0;
  }

  .residency-details {
    background: #f8fafc;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .residency-name {
    font-weight: 500;
    color: #2d3748;
  }

  .residency-id {
    font-size: 12px;
    color: #718096;
    margin-top: 4px;
  }

  /* Footer stays pinned at bottom */
  .grand-total {
    padding: 16px 24px 24px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom)); /* iPhone safe area */
    background-color: #ffffff;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    flex-shrink: 0; /* CRITICAL: Prevents footer from shrinking */
    box-shadow: 0 -4px 16px rgba(0,0,0,0.06);
    z-index: 10;
  }

  .total-divider {
    margin: 0 0 16px 0;
    display: none; 
  }

  .total-amount-row {
    display: flex;
    justify-content: space-between;
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
    text-transform: uppercase;
  }

  .total-amount-row .amount {
    color: #74a533;
    font-size: 24px;
  }

  .checkout-section {
    margin-top: 16px;
  }

  .checkout-btn {
    border-radius: 8px !important;
    font-weight: 700 !important;
    letter-spacing: 1.5px !important;
    text-transform: uppercase !important;
    padding: 18px !important;
    font-size: 16px !important;
    box-shadow: 0 6px 18px rgba(116, 165, 51, 0.4) !important;
    transition: all 0.3s ease !important;
  }

  .checkout-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(116, 165, 51, 0.5) !important;
  }

  .checkout-btn:active {
    transform: translateY(0);
  }

  .payment-header {
    padding: 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .loading-container {
    display: flex;
    justify-content: center;
    padding: 40px;
  }

  .payment-details {
    padding: 24px;
  }

  .payment-amount {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 24px;
  }

  .payment-amount .currency {
    font-size: 24px;
    font-weight: 500;
    color: #4a5568;
    margin-right: 8px;
  }

  .payment-amount .amount {
    font-size: 36px;
    font-weight: 700;
    color: #74a533;
  }

  .donation-section {
    padding-top: 24px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .donation-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #4a5568;
    text-align: center;
    margin: 0 0 16px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .donation-item {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }

  .donation-amount {
    font-weight: 500;
    color: #2d3748;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  /* ==================================================
     MEDIA QUERIES FOR RESPONSIVENESS
  ================================================== */
  @media (max-width: 960px) {
    .summary-title, .payment-title {
      font-size: 20px;
    }

    .summary-dates {
      font-size: 14px;
    }

    .section-title {
      font-size: 14px;
    }
    
    .logged-in-user-banner {
      padding: 12px;
    }
    
    .user-name {
      font-size: 14px;
    }
  }

  @media (max-width: 600px) {
    .summary-header, .payment-header {
      padding: 16px;
    }

    .summary-sections, .payment-details {
      padding: 0 16px;
    }

    .grand-total {
      padding: 16px;
      padding-bottom: calc(16px + env(safe-area-inset-bottom));
    }

    .payment-amount .amount {
      font-size: 28px;
    }

    .summary-title, .payment-title {
      font-size: 18px;
    }

    .section-title {
      font-size: 13px;
    }

    .item-name {
      font-size: 14px;
    }

    .item-price {
      font-size: 14px;
    }

    .delete-btn {
      width: 24px !important;
      height: 24px !important;
    }
    
    .logged-in-user-banner {
      flex-wrap: wrap;
      gap: 8px;
    }
    
    .logout-btn {
      margin-left: auto;
    }

    .checkout-btn {
      padding: 16px !important;
      font-size: 15px !important;
    }
  }

  @media (max-width: 400px) {
    .summary-header, .payment-header {
      padding: 12px;
    }

    .summary-title, .payment-title {
      font-size: 16px;
    }

    .section-icon {
      margin-right: 8px;
      font-size: 18px;
    }

    .item-row {
      padding: 8px 0;
    }

    .delete-btn {
      margin-left: 8px;
    }
  }
</style>